import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDownTrayIcon, TableCellsIcon } from '@heroicons/react/20/solid';
import ToolbarButton from '@ui/button/ToolbarButton';
import ButtonTabs from '@ui/tabs/ButtonTabs';
import CaptionGroup from '@ui/captiongroup/CaptionGroup';
import CheckboxMenu from '@ui/menu/CheckboxMenu';
import Message from '@ui/message/Message';
import KpiStrip from '@ui/chart/KpiStrip';
import DashboardPlaceholder from '@ui/placeholder/DashboardPlaceholder';
import { RoomTypeBasis } from '@app/model/bi/Bi';
import { useRoomTypeReport } from '@app/model/bi/service/BiHooks';
import BiService, { saveFile } from '@app/model/bi/service/BiService';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import BiPageHeader from '../fragment/BiPageHeader';
import { RoomTypeGridCard, RoomTypeMixCard } from '../fragment/RoomTypeAnalysisCards';
import { RoomTypeAllCard, RoomTypeByPropertyCard } from '../fragment/RoomTypeTableCards';
import { change, collapseRows, ColumnSet, Measure, revenue, today } from '../fragment/RoomTypes.utilities';
import { exportFileName, formatDateTime, periodPresets, propertyCount, shortName } from '../Bi.utilities';
import { useAccountBiScope, useBiAddress } from '../BiScopeHooks';
import { useBiWidthClass } from '../BiFullWidth';
import BiSwitchRow from '../fragment/BiSwitchRow';
import useColumnSets from '../useColumnSets';

type View = 'ANALYSIS' | 'TABLE';
type TableRows = 'ALL' | 'BY_PROPERTY';

const COLUMN_SETS: { value: ColumnSet, label: string }[] = [
	{ value: 'stly', label: 'STLY' }, { value: 'lyActual', label: 'LY actual' }, { value: 'vs', label: 'Revenue vs STLY and vs LY actual' },
	{ value: 'pu1', label: 'PU 1' }, { value: 'pu3', label: 'PU 3' }, { value: 'pu7', label: 'PU 7' }, { value: 'pu14', label: 'PU 14' }, { value: 'pu30', label: 'PU 30' },
];
const BLOCKS = COLUMN_SETS.map(c => c.value);
// Decision 14: STLY, LY actual, both vs % and PU 7.
const DEFAULT_SETS: ColumnSet[] = ['stly', 'lyActual', 'vs', 'pu7'];
const BASIS_NOTE: Record<RoomTypeBasis, string> = {
	BOOKED: 'Room type is based on the room originally booked, not the room later assigned in the PMS. A night booked on a virtual room type (Overbooking) counts under its assigned room type.',
	ASSIGNED: 'Room type is based on the room assigned in the PMS, not necessarily the room type originally booked.',
};

// The Room Types report (BI report 2, ROO2-262, direction A of the canvas): which room types sell, at what rate, how full
// each type is, and how the result compares with last year. One period, "Stay months", and the revenue switches of the
// Segmentation report; the room type basis Booked (the default) or Assigned for every figure.
const BiRoomTypesPage = () => {
	const scope = useAccountBiScope();
	const address = useBiAddress(scope.data);
	const [view, setView] = useState<View>('ANALYSIS');
	const [basis, setBasis] = useState<RoomTypeBasis>('BOOKED');
	const [measure, setMeasure] = useState<Measure>('revenueShare');
	const [sets, toggle] = useColumnSets('ROOM_TYPES', BLOCKS, DEFAULT_SETS);
	const width = useBiWidthClass();
	const [transposed, setTransposed] = useState(false);
	const [tableRows, setTableRows] = useState<TableRows>('BY_PROPERTY');
	const report = useRoomTypeReport(address.companyHotelId, address.propertyGroupId, address.period.from, address.period.to, basis,
		scope.data !== undefined && address.ready);
	const toaster = useToaster();
	// true while the Excel file downloads: Export to Excel shows "Preparing…" and is disabled.
	const [exporting, setExporting] = useState(false);
	const { first, last } = address;
	const presets = useMemo(() => periodPresets(address.asOfMonth, first, last), [address.asOfMonth, first, last]);

	if (scope.isError) return <main className={`${width} px-4 py-6 sm:px-6 lg:px-8`}>
		<Message state="error" title="The Room Types report did not load" flush>Reload the page. If the problem stays, contact RoomPulse.</Message>
	</main>;
	if (scope.data === undefined) return <main className={`${width} px-4 py-6 sm:px-6 lg:px-8`}><DashboardPlaceholder /></main>;

	const s = scope.data;
	// More than one property: All properties or a Property group (the grid and the mix collapse, the table has By property).
	const all = address.companyHotelId === null;
	const withData = address.properties.some(p => p.reservationsSource !== null);
	const sources = [...new Set(address.properties.map(p => p.reservationsSource).filter((v): v is string => v !== null))];
	const sublabel = all ? `${propertyCount(address.properties.length)} · Room types` : `${address.property?.area ?? ''} · Room types`;
	// The name of the scope in the file name: the property, the Property group, or null (All properties).
	const scopeName = address.property !== null ? shortName(address.property, s.companyName) : (address.propertyGroup?.title ?? null);
	const state = report.isError ? 'error' as const : (report.data === undefined ? 'loading' as const : (report.isPlaceholderData ? 'refreshing' as const : 'default' as const));
	const data = report.data;
	const scopeR = address.revenueScope, vat = address.vat;
	const missingNames = data === undefined ? [] : data.lastYearMissing
		.map(id => s.properties.find(p => p.companyHotelId === id)).filter((p): p is NonNullable<typeof p> => p !== undefined)
		.map(p => shortName(p, s.companyName));
	// ROO2-271 D1: with a property that has no last-year stays, the changes are like-for-like.
	const lfl = missingNames.length > 0;
	const vsStly = lfl ? 'vs STLY, like-for-like' : 'vs STLY';
	// Decision 22: with All properties the grid and the mix show the 12 largest types and "All other"; the table keeps all.
	const analysisData = data === undefined || !all ? data : collapseRows(data, scopeR, vat);
	const total = data?.total;
	// While the report loads, the four tiles show their labels over the loading skeleton of the strip.
	const kpis = total === undefined ? [
		{ id: 'types', label: 'Room types', value: null, format: 'number' as const },
		{ id: 'rn', label: 'Room nights', value: null, format: 'number' as const },
		{ id: 'rev', label: 'Revenue', value: null, format: 'money' as const },
		{ id: 'res', label: 'Reservations', value: null, format: 'number' as const },
	] : [
		{ id: 'types', label: 'Room types', value: data?.rows.filter(r => r.kind === 'ROOM_TYPE' && today(r).roomNights > 0).length ?? 0, format: 'number' as const },
		{ id: 'rn', label: 'Room nights', value: today(total).roomNights, format: 'number' as const,
			change: total.stly.roomNights === 0 ? undefined : { value: (total.comparable.roomNights - total.stly.roomNights) / total.stly.roomNights, format: 'percent' as const, label: vsStly } },
		{ id: 'rev', label: 'Revenue', value: revenue(today(total), scopeR, vat), format: 'money' as const,
			change: (() => { const c = change(revenue(total.comparable, scopeR, vat), total.stly, scopeR, vat); return c === null ? undefined : { value: c, format: 'percent' as const, label: vsStly }; })() },
		{ id: 'res', label: 'Reservations', value: total.reservations, format: 'number' as const },
	];

	// The Excel file (RQ-32, decision 26): the server builds the sheets from the same report, for the scope, the period, the
	// basis and the revenue switches of the page; not the Columns menu or Rows | Transposed (the file holds every column).
	// Export to Excel waits for the table: it is off while the report loads, shows the old period dimmed, or failed.
	const exportReady = data !== undefined && !report.isPlaceholderData && !report.isError && !exporting;
	const exportFile = () => {
		setExporting(true);
		BiService.downloadRoomTypesExcel({
			companyHotelId: address.companyHotelId, propertyGroupId: address.propertyGroupId, stayFrom: address.period.from, stayTo: address.period.to,
			basis, scope: scopeR, vat,
		})
			.then(blob => saveFile(blob, exportFileName(scopeName, address.period, s.asOf, 'Room types')))
			.catch(() => toaster.error('Export to Excel', 'The Excel file did not download. Try again. If the problem stays, contact RoomPulse.'))
			.finally(() => setExporting(false));
	};

	const tableControls = <>
		{all && <ButtonTabs label="Rows of the table" value={tableRows} onChange={v => setTableRows(v as TableRows)}
			options={[{ value: 'ALL', label: 'All' }, { value: 'BY_PROPERTY', label: 'By property' }]} />}
		{(!all || tableRows === 'ALL') && <ButtonTabs label="Layout of the table" value={transposed ? 'T' : 'R'} onChange={v => setTransposed(v === 'T')}
			options={[{ value: 'R', label: 'Rows' }, { value: 'T', label: 'Transposed' }]} />}
		{(!all || tableRows === 'ALL') && <CheckboxMenu label={<><TableCellsIcon aria-hidden="true" className="h-4 w-4 text-gray-400" />Columns<span className="font-normal text-gray-500">· {sets.length}</span></>}
			options={COLUMN_SETS} selected={sets} onToggle={toggle} anchor="bottom end" variant="button" />}
		{/* ToolbarButton (design system v1.19.0): the 36 px look of the Columns button beside it, as the Segmentation export. */}
		<ToolbarButton icon={<ArrowDownTrayIcon className="h-4 w-4" />} disabled={!exportReady} onClick={exportFile}>
			{exporting ? 'Preparing…' : 'Export to Excel'}
		</ToolbarButton>
	</>;

	return <>
		<BiPageHeader scope={s} companyHotelId={address.companyHotelId} propertyGroup={address.propertyGroup} groups={address.groups}
			canEditGroups={address.canEditGroups} search={address.search} sublabel={sublabel} page="Room types" fullWidthSwitch>
			{address.importedAt !== null && <span className="whitespace-nowrap text-sm text-gray-500">Reservations as of{' '}
				<span className="font-semibold text-gray-900">{formatDateTime(address.importedAt)}</span></span>}
		</BiPageHeader>
		<main className={`${width} flex flex-col gap-5 px-4 py-6 sm:px-6 lg:px-8`}>
			{!withData && <Message state="info" title="No reservations for this scope" flush>
				<p>The Room Types report reads the room types of the imported reservations. Connect a Reservations integration in{' '}
					<Link to={all ? '/secured/settings/integrations' : `/secured/settings/integrations/${address.companyHotelId ?? ''}#reservations`}
						className="font-medium underline">Settings › Integrations</Link>.</p>
			</Message>}
			{withData && <>
				<BiSwitchRow scope={scopeR} vat={vat} onScope={v => address.set({ revenue: v === 'TOTAL' ? 'total' : 'room' })}
					onVat={v => address.set({ vat: v === 'NET' ? 'net' : 'incl' })} period={address.period}
					onPeriod={range => address.set({ from: range.from, to: range.to })} presets={presets} min={first} max={last}
					line={BASIS_NOTE[basis]} sources={sources}>
					<CaptionGroup caption="View" className="col-span-2 w-full sm:col-span-1 sm:w-auto">
						<div className="w-full min-w-0">
							<ButtonTabs label="View of the report" value={view} onChange={v => setView(v as View)}
								options={[{ value: 'ANALYSIS', label: 'Analysis' }, { value: 'TABLE', label: 'Table' }]} />
						</div>
					</CaptionGroup>
					<CaptionGroup caption="Room type" className="col-span-2 w-full sm:col-span-1 sm:w-auto">
						<div className="w-full min-w-0">
							<ButtonTabs label="Room type of each figure" value={basis} onChange={v => setBasis(v as RoomTypeBasis)}
								options={[{ value: 'BOOKED', label: 'Booked' }, { value: 'ASSIGNED', label: 'Assigned' }]} />
						</div>
					</CaptionGroup>
				</BiSwitchRow>
				<div>
					<KpiStrip ariaLabel="Room types: headline figures" items={kpis} state={data === undefined ? 'loading' : 'default'} />
					{/* ROO2-271 D1-c: KpiStrip shows the change label only as the chip tooltip, so the page says it in one line. */}
					{lfl && <p className="mt-2 text-xs text-gray-500">The changes are like-for-like: they leave out {missingNames.length === 1 ? 'a property' : `${missingNames.length} properties`} with no stays in the last-year period: {missingNames.join(', ')}.</p>}
				</div>
				{view === 'ANALYSIS' && <>
					<RoomTypeGridCard report={analysisData} totalReport={data} period={address.period} asOf={s.asOf} measure={measure} onMeasure={setMeasure}
						scope={scopeR} vat={vat} state={state} onRetry={() => void report.refetch()} />
					<RoomTypeMixCard report={analysisData} period={address.period} scope={scopeR} vat={vat} state={state} onRetry={() => void report.refetch()} />
				</>}
				{view === 'TABLE' && (all && tableRows === 'BY_PROPERTY'
					? <RoomTypeByPropertyCard report={data} period={address.period} properties={address.properties} companyName={s.companyName}
						scope={scopeR} vat={vat} state={state} onRetry={() => void report.refetch()} actions={tableControls} />
					: <RoomTypeAllCard report={data} period={address.period} columnSets={sets} transposed={transposed} scope={scopeR} vat={vat} asOf={s.asOf}
						missingNames={missingNames} state={state} onRetry={() => void report.refetch()} actions={tableControls} />)}
			</>}
		</main>
	</>;
};

export default BiRoomTypesPage;
